import { Link } from 'components/lib';
import { Box, Typography } from '@mui/material';
import React, { ReactElement, ReactNode } from 'react';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { SubMenuItem } from './SubMenuItem';

export interface SideMenuLinkProps {
  label: ReactNode;
  href: string;
  icon?: ReactElement;
  active?: boolean;
  expanded?: boolean;
  children?: ReactNode;
  className?: string;
  onClick?: () => void;
  onClickExpand?: (e: React.MouseEvent) => void;
  badge?: number;
}

/** Figma Sidebar/Nav Item — 8429:4153 / Chrome/Sidebar 8429:5205 */
export const SideMenuLink = ({
  label,
  href,
  icon,
  expanded = false,
  active = false,
  children,
  className,
  onClick,
  onClickExpand,
  badge,
}: SideMenuLinkProps): ReactElement => {
  const showBadge = badge !== undefined && badge > 0;
  const hasChildren = Boolean(children);

  const handleRowClick = (e: React.MouseEvent) => {
    if (!hasChildren) return;
    e.preventDefault();
    e.stopPropagation();
    onClickExpand?.(e);
  };

  const iconAndLabel = (
    <>
      {/*
        Match prod SideMenuLink icon slot (40px). Do NOT force SVG width/height —
        Add Org is a 33×25 asset and squashing it makes the arc/circle overlap.
        Only constrain <img> menu icons (objectFit:cover otherwise crops them).
      */}
      <Box
        width={40}
        minWidth={40}
        display="flex"
        justifyContent="center"
        alignItems="center"
        flexShrink={0}
        sx={{
          lineHeight: 0,
          overflow: 'visible',
          '& img': {
            maxWidth: '33px',
            maxHeight: '25px',
            width: 'auto !important',
            height: 'auto !important',
            objectFit: 'contain !important',
            display: 'block',
          },
        }}
      >
        {icon}
      </Box>
      <Typography
        sx={{
          flex: 1,
          minWidth: 0,
          fontFamily: 'Poppins, sans-serif',
          fontSize: '14px',
          lineHeight: '22px',
          fontWeight: active ? 500 : 400,
          color: active ? '#242D56' : '#5F6375',
        }}
      >
        {label}
      </Typography>
    </>
  );

  return (
    <Box
      className={className}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        width: '100%',
      }}
    >
      <Box
        onClick={hasChildren ? handleRowClick : undefined}
        role={hasChildren ? 'button' : undefined}
        aria-expanded={hasChildren ? expanded : undefined}
        aria-label={
          hasChildren
            ? expanded
              ? `Collapse ${typeof label === 'string' ? label : 'section'}`
              : `Expand ${typeof label === 'string' ? label : 'section'}`
            : undefined
        }
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          gap: '12px',
          pr: '16px',
          py: '12px',
          boxSizing: 'border-box',
          cursor: hasChildren ? 'pointer' : 'default',
          backgroundColor: active ? '#F7F7F9' : 'transparent',
          '@media (hover: hover)': {
            '&:hover': {
              backgroundColor: '#F7F7F9',
            },
          },
        }}
      >
        <Box
          aria-hidden
          sx={{
            width: '3px',
            height: '20px',
            flexShrink: 0,
            backgroundColor: active ? '#242D56' : 'transparent',
          }}
        />

        {hasChildren ? (
          <Box
            sx={{
              display: 'flex',
              flex: 1,
              minWidth: 0,
              alignItems: 'center',
              gap: '8px',
            }}
          >
            {iconAndLabel}
          </Box>
        ) : (
          <Link
            className="link"
            href={href}
            onClick={onClick}
            sx={{
              '&&': {
                display: 'flex',
                flex: 1,
                minWidth: 0,
                alignItems: 'center',
                gap: '8px',
                textDecoration: 'none',
                whiteSpace: 'nowrap',
                backgroundColor: 'transparent',
                fontWeight: 'inherit',
                '&:hover': {
                  backgroundColor: 'transparent',
                  textDecoration: 'none',
                },
              },
            }}
          >
            {iconAndLabel}
          </Link>
        )}

        {showBadge && (
          <Box
            sx={{
              backgroundColor: '#BE3A34',
              color: '#FFFFFF',
              borderRadius: '10px',
              minWidth: '20px',
              height: '22px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: '7px',
              py: '2px',
              fontSize: '12px',
              lineHeight: '18px',
              fontWeight: 400,
              fontFamily: 'Poppins, sans-serif',
              flexShrink: 0,
              boxSizing: 'border-box',
            }}
          >
            {badge}
          </Box>
        )}

        {hasChildren && (
          <Box
            lineHeight={0}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '28px',
              height: '28px',
              flexShrink: 0,
              pointerEvents: 'none',
            }}
            aria-hidden
          >
            {expanded ? (
              <ExpandLess sx={{ fontSize: 16, color: '#242D56' }} />
            ) : (
              <ExpandMore sx={{ fontSize: 16, color: '#5F6375' }} />
            )}
          </Box>
        )}
      </Box>
      {hasChildren && expanded ? children : null}
    </Box>
  );
};

interface SideMenuLink {
  (props: SideMenuLinkProps): ReactElement;
  SubMenuItem: typeof SubMenuItem;
}

SideMenuLink.SubMenuItem = SubMenuItem;
